import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Pen } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useComplaint, useCreateComplaint, useUpdateComplaint } from '../hooks';

const complaintSchema = z.object({
  comment: z.string().min(1, 'Comment is required'),
  userId: z.number({ invalid_type_error: 'User is required' }),
  clientId: z.number({ invalid_type_error: 'Client is required' }),
});

type FormValues = z.infer<typeof complaintSchema>;

interface CreateComplaintDialogProps {
  id?: number;
}

export function CreateComplaintDialog({ id }: CreateComplaintDialogProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const { data: existingComplaint } = useComplaint(id || 0);
  const { mutate: createComplaint, isPending: isCreating } = useCreateComplaint();
  const { mutate: updateComplaint, isPending: isUpdating } = useUpdateComplaint();

  const form = useForm<FormValues>({
    resolver: zodResolver(complaintSchema),
    defaultValues: {
      comment: '',
      userId: undefined,
      clientId: undefined,
    },
  });

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (open && id && existingComplaint) {
      form.reset({
        comment: existingComplaint.comment || '',
        userId: existingComplaint.userId,
        clientId: existingComplaint.clientId,
      });
    }
  }, [open, id, existingComplaint, form]);

  const onSubmit = (values: FormValues, action: 'create' | 'update' | 'createAndContinue' = 'create') => {
    if (action === 'update' && id) {
      updateComplaint(
        { id, data: values },
        { onSuccess: () => setOpen(false) }
      );
    } else {
      createComplaint(values, {
        onSuccess: () => {
          form.reset();
          if (action === 'create') setOpen(false);
        },
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {id ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen className="h-4 w-4" />
          </Button>
        ) : (
          <Button>
            {t('new')} {t('complaint')}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent className="p-6 max-w-lg">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {id ? t('update') : t('new')} {t('complaint')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label={t('closeDialog')}>
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form className="mt-4 flex flex-col gap-4" noValidate>
            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('comment')} *</FormLabel>
                  <FormControl>
                    <Textarea placeholder={t('enterComment')} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="userId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('user')} *</FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/users"
                      listUrl="/users/select-box"
                      labelKey="name"
                      valueKey="id"
                      selectedId={field.value}
                      onSelectItem={(item) => field.onChange(item?.['id'])}
                      placeholder={t('selectUser')}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('client')} *</FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/clients"
                      labelKey="name"
                      labelKeys={['lastname', 'name', 'middlename']}
                      valueKey="id"
                      selectedId={field.value}
                      onSelectItem={(item) => field.onChange(item?.['id'])}
                      placeholder={t('selectClient')}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>

              {id ? (
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={form.handleSubmit((values) => onSubmit(values, 'update'))}
                >
                  {isSubmitting ? t('saving') : t('save')}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    variant="outline"
                    onClick={form.handleSubmit((values) => onSubmit(values, 'createAndContinue'))}
                  >
                    {isSubmitting ? t('saving') : t('saveAndCreate')}
                  </Button>
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={form.handleSubmit((values) => onSubmit(values, 'create'))}
                  >
                    {isSubmitting ? t('saving') : t('save')}
                  </Button>
                </>
              )}
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
